/**
 * The layers tree — the Figma-style outline of the active page.
 *
 * Rendered from a FLATTENED row list rather than a recursive template: it keeps
 * the DOM shallow, makes keyboard navigation and drag-reorder a matter of index
 * arithmetic, and lets collapsed subtrees simply not emit rows.
 *
 * Rows are listed top-of-stack first (last node in the array renders on top, so
 * it belongs at the top of the list) — the order designers expect.
 */

import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';

import {
  DESIGN_LAYER_DRAG_TYPE,
  type DesignLayer,
  type DesignLayerKind,
  type DesignNode,
} from '../../design-builder.types';
import { acceptsChildren } from '../../utils/design-node.utils';
import { LAYER_KIND_ICONS, LAYER_KIND_LABELS, LAYER_KIND_ORDER } from '../../utils/design-layer.utils';
import { WIDGET_PALETTE_META } from '../../design-builder.constants';

/** A node of the tree, or the header of one of a screen's layers. */
export type LayerRow = NodeRow | LayerGroupRow;

export interface NodeRow {
  type: 'node';
  id: string;
  node: DesignNode;
  depth: number;
  hasChildren: boolean;
  expanded: boolean;
  /** Listed under a group as one of its members; the node itself lives in its
   *  own band, so this row only points at it. */
  reference?: boolean;
}

export interface LayerGroupRow {
  type: 'layer';
  id: string;
  layer: DesignLayer;
  /** The screen that owns it. */
  screenId: string;
  depth: number;
  count: number;
  expanded: boolean;
  /** Derived from its nodes — a layer keeps no flags of its own. */
  visible: boolean;
  locked: boolean;
}

export interface LayerDropEvent {
  dragId: string;
  targetId: string;
  /** `inside` reparents into a frame; before/after reorders as a sibling. */
  position: 'before' | 'after' | 'inside';
}

/** Prefix that tells a layer drag apart from a node drag on the same channel. */
const LAYER_DRAG_PREFIX = 'layer:';

const TYPE_ICONS: Record<string, string> = {
  FRAME: 'crop_free',
  GROUP: 'folder',
  RECT: 'rectangle',
  ELLIPSE: 'circle',
  LINE: 'horizontal_rule',
  TEXT: 'title',
  IMAGE: 'image',
  ICON: 'star',
};

@Component({
  selector: 'app-design-layers-panel',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, MatIconModule, MatTooltipModule],
  templateUrl: './layers-panel.component.html',
  styleUrl: './layers-panel.component.scss',
})
export class LayersPanelComponent {
  readonly nodes = input.required<readonly DesignNode[]>();
  readonly selectedIds = input<ReadonlySet<string>>(new Set());
  /** Where a new node is filed, highlighted in the tree. */
  readonly activeLayerId = input<string>('');

  readonly selectNode = output<{ id: string; additive: boolean }>();
  readonly toggleVisible = output<string>();
  readonly toggleLocked = output<string>();
  readonly renameNode = output<{ id: string; name: string }>();
  readonly deleteNode = output<string>();
  readonly reorderNode = output<LayerDropEvent>();

  readonly selectLayer = output<string>();
  readonly renameLayer = output<{ id: string; name: string }>();
  readonly setLayerFlag = output<{ id: string; flag: 'visible' | 'locked'; value: boolean }>();
  readonly deleteLayer = output<string>();
  readonly addLayer = output<{ screenId: string; kind: DesignLayerKind }>();
  readonly moveLayer = output<{ dragId: string; targetId: string; position: 'before' | 'after' }>();
  readonly moveNodeToLayer = output<{ nodeId: string; layerId: string }>();

  readonly kindLabels = LAYER_KIND_LABELS;

  /** Only kinds the screen has no band for — there is never a second one. */
  readonly addableKinds = computed<readonly DesignLayerKind[]>(() => {
    const screen = this.nodes().find((node) => node.layers);
    if (!screen) return [];
    const present = new Set((screen.layers ?? []).map((layer) => layer.kind));
    return LAYER_KIND_ORDER.filter((kind) => !present.has(kind));
  });

  /** Ids of collapsed containers — expanded is the default, so this stays small. */
  private readonly collapsed = signal<ReadonlySet<string>>(new Set());
  readonly editingId = signal<string | null>(null);
  readonly dragOverId = signal<string | null>(null);
  readonly dropPosition = signal<'before' | 'after' | 'inside'>('after');

  readonly rows = computed<LayerRow[]>(() => {
    const collapsed = this.collapsed();
    const rows: LayerRow[] = [];
    const byId = new Map<string, DesignNode>();
    for (const root of this.nodes()) {
      for (const child of root.children ?? []) byId.set(child.id, child);
    }

    const walk = (nodes: readonly DesignNode[], depth: number, parentKey = ''): void => {
      // Reverse: the LAST node paints on top, so it heads the list.
      for (const node of [...nodes].reverse()) {
        // The screen is the page, not a layer you work with — its bands ARE the
        // top level of this tree, so it gets no row of its own.
        if (node.layers) {
          walkLayers(node, depth);
          continue;
        }
        const members = node.memberIds?.length
          ? node.memberIds.map((id) => byId.get(id)).filter((member): member is DesignNode => !!member)
          : [];
        const children = members.length ? members : (node.children ?? []);
        const key = parentKey ? `${parentKey}:${node.id}` : node.id;
        const expanded = !!children.length && !collapsed.has(key);
        rows.push({
          type: 'node',
          id: key,
          node,
          depth,
          hasChildren: !!children.length,
          expanded,
          ...(parentKey ? { reference: true } : {}),
        });
        // A group's rows point at nodes that live in their own band, so they
        // are keyed by the path that got here, not by the node id.
        if (expanded) walk(children, depth + 1, members.length ? key : parentKey);
      }
    };

    const walkLayers = (screen: DesignNode, depth: number): void => {
      const children = screen.children ?? [];
      for (const layer of [...screen.layers!].reverse()) {
        const owned = children.filter((child) => child.layerId === layer.id);
        const expanded = !collapsed.has(layer.id);
        rows.push({
          type: 'layer',
          id: layer.id,
          layer,
          screenId: screen.id,
          depth,
          count: owned.length,
          expanded,
          visible: !owned.length || owned.some((child) => child.visible),
          locked: !!owned.length && owned.every((child) => child.locked),
        });
        if (expanded && owned.length) walk(owned, depth + 1);
      }
    };

    walk(this.nodes(), 0);
    return rows;
  });

  readonly isEmpty = computed(() => !this.rows().length);

  iconFor(node: DesignNode): string {
    if (node.type === 'WIDGET') {
      return WIDGET_PALETTE_META[node.widget?.layoutType ?? '']?.icon ?? 'widgets';
    }
    return TYPE_ICONS[node.type] ?? 'crop_square';
  }

  layerIconFor(layer: DesignLayer): string {
    return LAYER_KIND_ICONS[layer.kind] ?? 'layers';
  }

  isSelected(id: string): boolean {
    return this.selectedIds().has(id);
  }

  /** The screen a new layer is added to — the first one, which is all there is. */
  readonly screenId = computed(() => this.nodes().find((node) => node.layers)?.id ?? '');

  /** The node a row stands for. Reference rows are keyed by path, not by id. */
  nodeIdOf(row: LayerRow): string {
    return row.type === 'node' ? row.node.id : row.id;
  }

  onRowClick(row: LayerRow, event: MouseEvent): void {
    if (row.type === 'layer') {
      this.selectLayer.emit(row.id);
      return;
    }
    this.selectNode.emit({
      id: row.node.id,
      additive: event.shiftKey || event.ctrlKey || event.metaKey,
    });
  }

  toggleExpanded(id: string, event: Event): void {
    event.stopPropagation();
    const next = new Set(this.collapsed());
    if (next.has(id)) next.delete(id);
    else next.add(id);
    this.collapsed.set(next);
  }

  startRename(id: string, event: Event): void {
    event.stopPropagation();
    this.editingId.set(id);
  }

  commitRename(id: string, value: string): void {
    this.editingId.set(null);
    const trimmed = value.trim();
    if (!trimmed) return;
    const row = this.rows().find((candidate) => candidate.id === id);
    if (!row) return;
    if (row.type === 'layer') this.renameLayer.emit({ id, name: trimmed });
    else this.renameNode.emit({ id: row.node.id, name: trimmed });
  }

  onLayerFlag(row: LayerGroupRow, flag: 'visible' | 'locked', event: Event): void {
    event.stopPropagation();
    this.setLayerFlag.emit({ id: row.id, flag, value: flag === 'visible' ? !row.visible : !row.locked });
  }

  onAddLayer(kind: DesignLayerKind): void {
    const screenId = this.screenId();
    if (screenId) this.addLayer.emit({ screenId, kind });
  }

  // ── Drag to reorder / reparent ─────────────────────────────────────────────

  onDragStart(row: LayerRow, event: DragEvent): void {
    // A reference row is a pointer, not the node — dragging it would be a lie.
    if (row.type === 'node' && row.reference) {
      event.preventDefault();
      return;
    }
    const payload = row.type === 'layer' ? `${LAYER_DRAG_PREFIX}${row.id}` : row.node.id;
    event.dataTransfer?.setData(DESIGN_LAYER_DRAG_TYPE, payload);
    if (event.dataTransfer) event.dataTransfer.effectAllowed = 'move';
  }

  /**
   * The drop zone depends on where in the row height the cursor sits: the top
   * and bottom quarters reorder as a sibling, the middle drops INTO a frame —
   * or, on a layer header, moves the node into that layer.
   */
  onDragOver(row: LayerRow, event: DragEvent): void {
    if (!event.dataTransfer?.types.includes(DESIGN_LAYER_DRAG_TYPE)) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';

    const bounds = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const ratio = (event.clientY - bounds.top) / bounds.height;
    const canNest = row.type === 'layer' || (!row.reference && acceptsChildren(row.node));

    this.dragOverId.set(row.id);
    this.dropPosition.set(
      ratio < 0.25 ? 'before' : ratio > 0.75 || !canNest ? 'after' : 'inside',
    );
  }

  onDragLeave(row: LayerRow): void {
    if (this.dragOverId() === row.id) this.dragOverId.set(null);
  }

  onDrop(row: LayerRow, event: DragEvent): void {
    const payload = event.dataTransfer?.getData(DESIGN_LAYER_DRAG_TYPE) ?? '';
    const position = this.dropPosition();
    const target = this.nodeIdOf(row);
    this.dragOverId.set(null);
    if (!payload || payload === target || payload === `${LAYER_DRAG_PREFIX}${row.id}`) return;
    event.preventDefault();

    const draggedLayer = payload.startsWith(LAYER_DRAG_PREFIX);
    if (draggedLayer) {
      // A layer only reorders against another layer — the screen's paint order.
      if (row.type === 'layer' && position !== 'inside') {
        this.moveLayer.emit({ dragId: payload.slice(LAYER_DRAG_PREFIX.length), targetId: row.id, position });
      }
      return;
    }

    if (row.type === 'layer') {
      this.moveNodeToLayer.emit({ nodeId: payload, layerId: row.id });
      return;
    }
    // Dropping onto a reference row would reorder inside a group, which holds
    // no order of its own — the node's own band decides that.
    if (row.reference) return;
    this.reorderNode.emit({ dragId: payload, targetId: target, position });
  }

  onDragEnd(): void {
    this.dragOverId.set(null);
  }

  dropHintFor(row: LayerRow): string {
    if (this.dragOverId() !== row.id) return '';
    return this.dropPosition();
  }

  trackByRow(_: number, row: LayerRow): string {
    return row.id;
  }
}
